import AiAssistant from './AiAssistant';
import MobileQuickActionsFab from './MobileQuickActionsFab';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  LogOut,
  Menu,
  X,
  Plane,
  Ticket,
  Clock,
  CheckCircle,
  ChevronRight,
  Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { adminNavItems, gestorNavItemsConfig } from './navConfig';
import NotificationToggle from './NotificationToggle';
import NotificationsBell from './NotificationsBell';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persist menu open/close state across reloads so the CEO doesn't have to
// re-expand the same category every time. Falls back to the active route's
// category when nothing is stored.
const MENU_STATE_KEY = 'aircom.adminNav.expanded';
const loadStoredExpanded = () => {
  try {
    const raw = localStorage.getItem(MENU_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Layout = () => {
  const { user, logout, isAdmin, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickStats, setQuickStats] = useState(null);

  useEffect(() => {
    fetchQuickStats();
  }, [location.pathname]);

  const fetchQuickStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/ventas/stats`, {
        headers: getAuthHeaders()
      });
      setQuickStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const gestorNavItems = gestorNavItemsConfig(user);

  // Categorías colapsables del menú admin.
  // Default: solo se abre la categoría que contiene la ruta activa.
  // Si el CEO expande otra categoría, lo persistimos en localStorage.
  const initialExpanded = (() => {
    const stored = loadStoredExpanded();
    if (stored && typeof stored === 'object') return stored;
    const initial = {};
    for (const cat of adminNavItems) {
      initial[cat.category] = cat.items.some((it) => {
        if (it.path === '/') return location.pathname === '/';
        return location.pathname.startsWith(it.path);
      });
    }
    return initial;
  })();
  const [expandedCategories, setExpandedCategories] = useState(initialExpanded);
  const toggleCategory = (name) => {
    setExpandedCategories((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      try { localStorage.setItem(MENU_STATE_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        sidebar fixed lg:static inset-y-0 left-0 z-50 w-72 
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                <Plane className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <h1 className="font-bold text-white text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Aircom Connect
                </h1>
                <p className="text-xs text-slate-400">
                  {isAdmin() ? 'Admin Panel' : 'Portal Gestor'}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats - Only for Admin */}
          {isAdmin() && quickStats && (
            <div className="px-4 py-3 border-b border-white/10">
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => navigate('/ventas?status=pending')}
                  className="flex items-center gap-2 p-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 transition-colors"
                >
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <div className="text-left">
                    <p className="text-xs text-yellow-200">Pendientes</p>
                    <p className="text-lg font-bold text-yellow-400">{quickStats.pending}</p>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/ventas?status=completed')}
                  className="flex items-center gap-2 p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors"
                >
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <div className="text-left">
                    <p className="text-xs text-green-200">Completados</p>
                    <p className="text-lg font-bold text-green-400">{quickStats.completed}</p>
                  </div>
                </button>
              </div>
              <button 
                onClick={() => navigate('/ventas')}
                className="w-full mt-2 p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-blue-200">Total Vendido</span>
                </div>
                <span className="text-lg font-bold text-blue-400">${quickStats.total_sales?.toLocaleString()}</span>
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
            {isAdmin() ? (
              // Admin categorized navigation — colapsable
              adminNavItems.map((category) => {
                const open = !!expandedCategories[category.category];
                return (
                  <div key={category.category}>
                    <button
                      type="button"
                      onClick={() => toggleCategory(category.category)}
                      className="w-full flex items-center justify-between px-3 mb-1.5 group"
                      data-testid={`nav-cat-toggle-${category.category.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider group-hover:text-slate-300">
                        {category.category}
                      </span>
                      <ChevronRight
                        className={`w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                      />
                    </button>
                    {open && (
                      <div className="space-y-1 mb-3">
                        {category.items.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === '/'}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) => `
                              sidebar-link ${isActive ? 'active' : ''}
                            `}
                            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                          >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                            <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Gestor simple navigation
              <div className="space-y-1">
                {gestorNavItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/mis-ventas'}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `
                      sidebar-link ${isActive ? 'active' : ''}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <NotificationToggle />
            <div className="flex items-center gap-3 px-3 py-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${isAdmin() ? 'bg-yellow-500' : 'bg-blue-600'}`}>
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">
                  {isAdmin() ? 'Administrador' : 'Gestor de Ventas'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-white/10 mt-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 flex items-center gap-4 sticky top-0 z-30">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          {/* Quick navigation for admin */}
          {isAdmin() && (
            <div className="hidden md:flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/ventas?status=pending')}
                className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
              >
                <Clock className="w-4 h-4 mr-1" />
                Pendientes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/ventas')}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Receipt className="w-4 h-4 mr-1" />
                Historial
              </Button>
            </div>
          )}
          
          <div className="flex-1" />
          <NotificationsBell />
          <div className="text-sm text-slate-500 hidden md:block">
            {new Date().toLocaleDateString('es-ES', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
        {user?.role === 'admin' && <AiAssistant />}
        <MobileQuickActionsFab />
      </div>
    </div>
  );
};

export default Layout;
